import React, { useCallback, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react'
import { dismissModal } from '../../service/modal-service'
import { ZoomableImage } from './ZoomableImage'

// Only for hosted players (YouTube, Vimeo, ...); uploaded videos play in a
// plain <video>, so the viewer no longer ships react-player for every photo
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })
const HOSTED_VIDEO = /youtube\.com|youtu\.be|vimeo\.com|facebook\.com|twitch\.tv/i

const kindOf = (type = '', url = '') => {
    if (HOSTED_VIDEO.test(url)) return 'hosted'
    if (type.includes('video')) return 'video'
    return 'image'
}

/**
 * MediaViewer - full-screen viewer for photos and videos, shared across the
 * app (chats, Buzz posts, profiles, request cards).
 *
 * - Photos: pinch / double-tap zoom and pan (ZoomableImage).
 * - Videos: native player with controls.
 * - Several items: swipe, arrow buttons or ← → keys, with a counter.
 * - Escape or × closes; "Open" shows the original in a new tab.
 * - Grows out of the point that was tapped (imageX / imageY, from
 *   js/open-image.js openImageHandler).
 *
 * Single item:  <MediaViewer media={url} mediaType="image/jpeg" imageX imageY />
 * Gallery:      <MediaViewer items={[{ url, type }]} index={2} imageX imageY />
 * Opened with openModal; closes with onClose (default dismissModal, i.e. back).
 */
export const MediaViewer = ({ media, mediaType = '', items, index = 0, imageX = 0, imageY = 0, onClose = dismissModal }) => {
    const list = items?.length ? items : [{ url: media, type: mediaType }]
    const [ current, setCurrent ] = useState(Math.min(Math.max(index, 0), list.length - 1))
    const item = list[current] || list[0]
    const kind = kindOf(item?.type, item?.url)
    const count = list.length

    const prev = useCallback(() => setCurrent(c => (c - 1 + count) % count), [count])
    const next = useCallback(() => setCurrent(c => (c + 1) % count), [count])

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose()
            else if (count > 1 && e.key === 'ArrowLeft') prev()
            else if (count > 1 && e.key === 'ArrowRight') next()
        }
        window.addEventListener('keydown', onKey)
        // The page behind doesn't scroll while viewing
        const overflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = overflow
        }
    }, [onClose, prev, next, count])

    if (!item?.url) return null

    return (
        <motion.div
            className="fixed inset-0 z-[55] flex flex-col bg-black text-white"
            role="dialog"
            aria-modal="true"
            aria-label={kind === 'image' ? 'Photo' : 'Video'}
            initial={{ x: imageX, y: imageY, scale: 0.2, opacity: 0 }}
            animate={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            exit={{ x: imageX, y: imageY, scale: 0.2, opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        >
            <div className="absolute inset-x-0 top-0 z-20 flex items-center gap-2 px-2 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 bg-linear-to-b from-black/70 to-transparent">
                <button type="button" onClick={onClose} className="w-11 h-11 rounded-full flex items-center justify-center bg-black/40 hover:bg-white/15" aria-label="Close">
                    <X className="size-6" aria-hidden="true" />
                </button>
                <p className="flex-1 text-center text-sm font-semibold" aria-live="polite">
                    {count > 1 ? `${current + 1} / ${count}` : ''}
                </p>
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="h-11 px-3 rounded-full bg-black/40 hover:bg-white/15 inline-flex items-center gap-1.5 text-sm font-semibold" aria-label="Open the original in a new tab">
                    <ExternalLink className="size-4.5" aria-hidden="true" /> Open
                </a>
            </div>

            <div className="relative flex-1 min-h-0">
                {kind === 'image' && (
                    <ZoomableImage
                        src={item.url}
                        alt={count > 1 ? `Photo ${current + 1} of ${count}` : 'Photo'}
                        imageKey={current}
                        onSwipeLeft={count > 1 ? next : undefined}
                        onSwipeRight={count > 1 ? prev : undefined}
                        showHint={false}
                        className="h-full w-full"
                    />
                )}
                {kind === 'video' && (
                    <div className="h-full w-full flex items-center justify-center">
                        <video key={item.url} src={item.url} controls autoPlay playsInline className="max-h-full max-w-full" />
                    </div>
                )}
                {kind === 'hosted' && (
                    <div className="h-full w-full flex items-center justify-center">
                        <ReactPlayer src={item.url} controls playing playsInline width="100%" height="100%" />
                    </div>
                )}

                {count > 1 && (
                    <>
                        <button type="button" onClick={prev} className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/50 hover:bg-white/20 items-center justify-center" aria-label="Previous">
                            <ChevronLeft className="size-7" aria-hidden="true" />
                        </button>
                        <button type="button" onClick={next} className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/50 hover:bg-white/20 items-center justify-center" aria-label="Next">
                            <ChevronRight className="size-7" aria-hidden="true" />
                        </button>
                    </>
                )}
            </div>

            {count > 1 && (
                <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center gap-1.5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6 bg-linear-to-t from-black/60 to-transparent" aria-hidden="true">
                    {list.map((_, i) => (
                        <span key={i} className={`h-1.5 rounded-full transition-all ${i === current ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
                    ))}
                </div>
            )}
        </motion.div>
    )
}

export default MediaViewer
